/**
 * Agency "GHL Connection" panel (Settings → Integrations). Replaces the old
 * webhook UI. Paste a Private Integration Token + location, "Test connection" to
 * pull pipelines / custom fields / workflows, toggle each operation, map form
 * fields, and view recent sync results. The token is write-only (never returned).
 */
import { api, ApiError } from './api.js';
import { esc } from './brand.js';
import { toast } from './toast.js';
import { enhanceSelects } from './combobox.js';

interface FormField { key: string; label: string; binding: string }
interface GhlConfig {
  location_id: string;
  upsert_enabled: boolean; tags_enabled: boolean; workflow_enabled: boolean; opportunity_enabled: boolean;
  workflow_id: string | null; pipeline_id: string | null; pipeline_name: string | null; stage_id: string | null;
  field_map: Record<string, string>; tags: string[]; coverage_as_tags: boolean;
  /** Set when GoHighLevel rejected the stored token; cleared by the next success. */
  auth_failed_at?: string | null;
  auth_failed_status?: number | null;
}
interface GhlGet { connected: boolean; cryptoReady: boolean; formFields: FormField[]; config: GhlConfig | null }
interface Pipeline { id: string; name: string; stages: { id: string; name: string }[] }
interface Workflow { id: string; name: string; status?: string }
interface Opts {
  pipelines: Pipeline[];
  customFields: { id: string; key?: string; name?: string }[];
  workflows: Workflow[];
  /** Why a list came back empty, when it came back empty for a reason. */
  workflowsNote?: string;
  customFieldsNote?: string;
}
interface SyncRow { submission_id: string; status: string; attempts: number; last_action: string | null; last_error: string | null; updated_at: string }

const DEFAULTS: GhlConfig = {
  location_id: '', upsert_enabled: true, tags_enabled: false, workflow_enabled: false, opportunity_enabled: false,
  workflow_id: null, pipeline_id: null, pipeline_name: null, stage_id: null, field_map: {}, tags: [], coverage_as_tags: false,
};

export async function mountGhlPanel(root: HTMLElement): Promise<void> {
  const host = root.querySelector('#ghlPanel') as HTMLElement | null;
  if (!host) return;
  host.innerHTML = '<div class="muted" style="padding:8px 0">Loading…</div>';

  let meta: GhlGet;
  try {
    meta = await api.get<GhlGet>('/api/agency/ghl');
  } catch (e) {
    host.innerHTML = e instanceof ApiError && e.status === 403
      ? '<div class="card"><div class="card-body"><div class="empty"><div>Only agency admins can manage integrations.</div></div></div></div>'
      : '<div class="note">Failed to load the GHL connection.</div>';
    return;
  }

  const state: GhlConfig = { ...DEFAULTS, ...(meta.config ?? {}) };
  let opts: Opts = { pipelines: [], customFields: [], workflows: [] };
  let log: SyncRow[] = [];

  // Read the current DOM into `state` so re-renders (toggles/test) don't lose edits.
  function collect(): void {
    const v = (id: string) => (host!.querySelector('#' + id) as HTMLInputElement | null)?.value ?? '';
    const chk = (id: string) => !!(host!.querySelector('#' + id) as HTMLInputElement | null)?.checked;
    state.location_id = v('ghl-loc').trim();
    state.upsert_enabled = chk('ghl-op-upsert');
    state.tags_enabled = chk('ghl-op-tags');
    state.workflow_enabled = chk('ghl-op-workflow');
    state.opportunity_enabled = chk('ghl-op-opportunity');
    state.tags = v('ghl-tags').split(',').map((t) => t.trim()).filter(Boolean);
    state.coverage_as_tags = chk('ghl-cov-tags');
    state.workflow_id = v('ghl-workflow') || null;
    state.pipeline_id = v('ghl-pipeline') || null;
    state.stage_id = v('ghl-stage') || null;
    const fm: Record<string, string> = {};
    host!.querySelectorAll<HTMLSelectElement>('select[data-fmap]').forEach((sel) => { if (sel.value) fm[sel.dataset.fmap!] = sel.value; });
    state.field_map = fm;
  }

  /**
   * Whether GHL is currently rejecting the stored token. Read from the server's
   * config rather than tracked locally: only a real sync or connection test can
   * establish it, and the server clears it on the next success.
   */
  const authRejected = (): boolean => Boolean(meta.connected && meta.config?.auth_failed_at);

  const authStatusText = (): string => {
    const s = meta.config?.auth_failed_status;
    if (s === 401) return ' (unauthorized)';
    if (s === 403) return ' (forbidden — check the token scopes)';
    return '';
  };

  const sel = (id: string, cur: string | null, items: { value: string; label: string }[], blank: string) =>
    `<select id="${id}"><option value="">${blank}</option>${items.map((i) => `<option value="${esc(i.value)}" ${i.value === cur ? 'selected' : ''}>${esc(i.label)}</option>`).join('')}</select>`;

  function render(): void {
    const cryptoWarn = meta.cryptoReady ? '' :
      `<div class="note" style="background:var(--danger-bg);border-color:#f5c6c2;color:#a3261c;margin-bottom:14px">Server token encryption isn't configured (<code>TENANT_TOKEN_KEY</code>). Saving a token will fail until an admin sets it.</div>`;

    // GoHighLevel rejected the stored token. Actionable by the person reading
    // this — re-entering the token below is the fix — so it is stated as a next
    // step rather than as an error code. Without this the only trace is a row in
    // the sync log nobody thinks to open.
    const authWarn = authRejected() ? `<div class="note" style="background:var(--danger-bg);border-color:#f5c6c2;color:#a3261c;margin-bottom:14px">
        GoHighLevel rejected this token${authStatusText()}, so leads are not syncing.
        It was most likely revoked or had its scopes narrowed — paste a new Private Integration Token below to reconnect.
      </div>` : '';

    const cfields = opts.customFields.filter((f) => f.key).map((f) => ({ value: f.key!, label: f.name || f.key! }));
    const pipeline = opts.pipelines.find((p) => p.id === state.pipeline_id) ?? opts.pipelines[0];
    const stageItems = (pipeline?.stages ?? []).map((s) => ({ value: s.id, label: s.name }));

    // GHL accepts an enrolment into an unpublished workflow and then does
    // nothing with it, so the state rides in the label. Anything that is not
    // published is called out by its own status rather than lumped as "draft",
    // since GHL has more than one of these.
    const workflowItems = opts.workflows.map((w) => ({
      value: w.id,
      label: w.status && w.status.toLowerCase() !== 'published' ? `${w.name} (${w.status})` : w.name,
    }));
    const hasUnpublished = opts.workflows.some((w) => w.status && w.status.toLowerCase() !== 'published');
    const workflowHint = opts.workflowsNote
      ? `<div class="muted" style="font-size:12px;margin-top:4px;color:#a3261c">${esc(opts.workflowsNote)}</div>`
      : hasUnpublished
        ? `<div class="muted" style="font-size:12px;margin-top:4px">Unpublished workflows are listed but will not run — publish one in GHL first.</div>`
        : '';
    const mapHint = opts.customFieldsNote
      ? `<div class="muted" style="font-size:12px;margin:0 0 8px;color:#a3261c">${esc(opts.customFieldsNote)}</div>`
      : '';

    const mapRows = meta.formFields.map((f) =>
      `<div class="row" style="justify-content:space-between;gap:10px;margin-bottom:6px">
        <span style="font-size:13px">${esc(f.label)} ${f.binding === 'custom' ? '<span class="muted">(custom)</span>' : ''}</span>
        ${sel(`fmap-${f.key}`, state.field_map[f.key] ?? '', cfields, '— not mapped —').replace('<select', `<select data-fmap="${esc(f.key)}" style="max-width:260px"`)}
      </div>`).join('');

    // A rejected token is still "connected" in the sense that one is stored, so
    // the badge has to distinguish it or the panel would claim everything is
    // fine while nothing syncs.
    const conn = authRejected()
      ? `<span class="badge red">token rejected</span>`
      : meta.connected
        ? `<span class="badge green">connected</span>`
        : `<span class="badge gray">not connected</span>`;

    host!.innerHTML = `
      ${cryptoWarn}
      ${authWarn}
      <div class="card"><div class="card-head"><h2>GHL connection ${conn}</h2>
        <button class="btn secondary sm" id="ghl-test">Test connection</button></div>
        <div class="card-body">
          <div class="grid-2">
            <div class="field"><label>Location ID</label><input type="text" id="ghl-loc" value="${esc(state.location_id)}" placeholder="GHL sub-account location id" /></div>
            <div class="field"><label>Private Integration Token</label><input type="password" id="ghl-pit" placeholder="${meta.connected ? '•••••• (leave blank to keep)' : 'paste token'}" autocomplete="off" /></div>
          </div>
          <div class="muted" id="ghl-test-status" style="font-size:12.5px;min-height:16px"></div>
        </div>
      </div>

      <div class="card" style="margin-top:16px"><div class="card-head"><h2>Operations</h2></div><div class="card-body">
        <label class="check"><input type="checkbox" id="ghl-op-upsert" ${state.upsert_enabled ? 'checked' : ''}/><span>Upsert contact <span class="muted">(required — name, email, phone, mapped fields)</span></span></label>
        <label class="check"><input type="checkbox" id="ghl-op-tags" ${state.tags_enabled ? 'checked' : ''}/><span>Apply tags</span></label>
        ${state.tags_enabled ? `<div class="field" style="margin:6px 0 10px 26px">
            <input type="text" id="ghl-tags" value="${esc(state.tags.join(', '))}" placeholder="tag-one, tag-two" />
            <label class="check" style="margin-top:6px"><input type="checkbox" id="ghl-cov-tags" ${state.coverage_as_tags ? 'checked' : ''}/><span style="font-size:13px">Also add a tag for each selected coverage</span></label></div>` : '<input type="hidden" id="ghl-tags" value="' + esc(state.tags.join(', ')) + '"><input type="hidden" id="ghl-cov-tags">'}
        <label class="check"><input type="checkbox" id="ghl-op-workflow" ${state.workflow_enabled ? 'checked' : ''}/><span>Add to workflow</span></label>
        ${state.workflow_enabled ? `<div class="field" style="margin:6px 0 10px 26px">${sel('ghl-workflow', state.workflow_id, workflowItems, '— select a workflow (Test first) —')}${workflowHint}</div>` : '<input type="hidden" id="ghl-workflow" value="' + esc(state.workflow_id ?? '') + '">'}
        <label class="check"><input type="checkbox" id="ghl-op-opportunity" ${state.opportunity_enabled ? 'checked' : ''}/><span>Create opportunity</span></label>
        ${state.opportunity_enabled ? `<div class="grid-2" style="margin:6px 0 4px 26px">
            <div class="field"><label>Pipeline</label>${sel('ghl-pipeline', state.pipeline_id, opts.pipelines.map((p) => ({ value: p.id, label: p.name })), '— select (Test first) —')}</div>
            <div class="field"><label>Stage</label>${sel('ghl-stage', state.stage_id, stageItems, '— select —')}</div></div>` : '<input type="hidden" id="ghl-pipeline" value="' + esc(state.pipeline_id ?? '') + '"><input type="hidden" id="ghl-stage" value="' + esc(state.stage_id ?? '') + '">'}
      </div></div>

      <div class="card" style="margin-top:16px"><div class="card-head"><h2>Field mapping</h2></div><div class="card-body">
        <p class="muted" style="margin-top:0;font-size:12.5px">Map your form fields to GHL custom fields. Name, email &amp; phone map to GHL's standard contact fields automatically. Run <strong>Test connection</strong> to load custom fields.</p>
        ${mapHint}
        ${mapRows || '<div class="muted">No form fields.</div>'}
      </div></div>

      <div class="row" style="gap:10px;margin-top:16px">
        <button class="btn" id="ghl-save">Save connection</button>
        ${meta.connected ? '<button class="btn ghost" id="ghl-disconnect">Disconnect</button>' : ''}
      </div>

      <div class="card" style="margin-top:22px"><div class="card-head"><h2>Recent syncs</h2>
        <button class="btn ghost sm" id="ghl-log-refresh">Refresh</button></div>
        <div class="card-body flush">${logTable(log)}</div></div>`;
    wire();
  }

  function wire(): void {
    const q = <T extends HTMLElement>(id: string) => host!.querySelector('#' + id) as T | null;
    // Toggles + pipeline change → collect then re-render (show/hide + repopulate stages).
    ['ghl-op-upsert', 'ghl-op-tags', 'ghl-op-workflow', 'ghl-op-opportunity', 'ghl-cov-tags', 'ghl-pipeline'].forEach((id) => {
      const el = q<HTMLInputElement>(id);
      if (el) el.onchange = () => { collect(); render(); };
    });

    const testBtn = q<HTMLButtonElement>('ghl-test');
    if (testBtn) testBtn.onclick = async () => {
      collect();
      const status = q<HTMLElement>('ghl-test-status')!;
      const pit = (q<HTMLInputElement>('ghl-pit')?.value ?? '').trim();
      if (!state.location_id) { status.textContent = 'Enter a location id first.'; return; }
      status.textContent = 'Testing…';
      try {
        opts = await api.post<Opts>('/api/agency/ghl/test', { location_id: state.location_id, pit: pit || undefined });
        // A count of zero used to be the only trace of a failed discovery call,
        // which read as "this location has none". Anything that actually failed
        // says so, on its own line, instead of being counted as none.
        const counts = [`${opts.pipelines.length} pipeline(s)`];
        if (!opts.customFieldsNote) counts.push(`${opts.customFields.length} custom field(s)`);
        if (!opts.workflowsNote) counts.push(`${opts.workflows.length} workflow(s)`);
        const notes = [opts.customFieldsNote, opts.workflowsNote].filter(Boolean) as string[];
        status.innerHTML =
          `<span style="color:var(--brand-primary)">✓ ${counts.join(', ')}</span>` +
          notes.map((n) => `<div style="color:#a3261c">${esc(n)}</div>`).join('');
        render();
      } catch (e) { status.innerHTML = `<span style="color:#a3261c">${esc(e instanceof ApiError ? e.message : 'Test failed')}</span>`; }
    };

    const saveBtn = q<HTMLButtonElement>('ghl-save');
    if (saveBtn) saveBtn.onclick = async () => {
      collect();
      if (!state.location_id) { toast('Location id is required', 'err'); return; }
      const pit = (q<HTMLInputElement>('ghl-pit')?.value ?? '').trim();
      if (!meta.connected && !pit) { toast('Paste a Private Integration Token to connect', 'err'); return; }
      saveBtn.disabled = true;
      try {
        await api.put('/api/agency/ghl', { ...state, pit: pit || undefined });
        toast('GHL connection saved');
        meta.connected = true;
        // Saving a token clears the rejected state server-side, so mirror that
        // here rather than leaving the warning up until the next page load.
        if (pit && meta.config) {
          meta.config.auth_failed_at = null;
          meta.config.auth_failed_status = null;
        }
        (q<HTMLInputElement>('ghl-pit')!).value = '';
        await loadLog(); render();
      } catch (e) { toast(e instanceof ApiError ? e.message : 'Save failed', 'err'); }
      finally { saveBtn.disabled = false; }
    };

    const disc = q<HTMLButtonElement>('ghl-disconnect');
    if (disc) disc.onclick = async () => {
      if (!confirm('Disconnect GHL? Submissions will stop syncing until you reconnect.')) return;
      try { await api.del('/api/agency/ghl'); toast('Disconnected'); Object.assign(state, DEFAULTS); meta.connected = false; opts = { pipelines: [], customFields: [], workflows: [] }; render(); }
      catch (e) { toast(e instanceof ApiError ? e.message : 'Failed', 'err'); }
    };

    const refresh = q<HTMLButtonElement>('ghl-log-refresh');
    if (refresh) refresh.onclick = async () => { await loadLog(); render(); };

    // Last, so the change handlers above are already attached: choosing from a
    // combobox fires `change` on the select underneath, which is what drives the
    // pipeline → stage re-render. Re-run per render because render() replaces
    // the markup, which discards the previous enhancement with it.
    enhanceSelects(host!);
  }

  async function loadLog(): Promise<void> {
    try { log = (await api.get<{ log: SyncRow[] }>('/api/agency/ghl/log')).log; } catch { /* leave as-is */ }
  }

  await loadLog();
  render();
  // Auto-populate selectors if already connected, so stored ids show their names.
  if (meta.connected && state.location_id) {
    try { opts = await api.post<Opts>('/api/agency/ghl/test', { location_id: state.location_id }); render(); } catch { /* user can Test manually */ }
  }
}

function logTable(log: SyncRow[]): string {
  if (!log.length) return '<div class="muted" style="padding:14px 20px">No syncs yet.</div>';
  const color = (s: string) => (s === 'ok' ? 'green' : s === 'error' ? 'amber' : 'gray');
  const rows = log.map((r) => `<tr>
    <td class="muted" style="font-size:12px">${esc(r.submission_id.slice(0, 10))}…</td>
    <td><span class="badge ${color(r.status)}">${esc(r.status)}</span></td>
    <td>${r.attempts}</td>
    <td class="muted" style="font-size:12px">${esc(r.last_action ?? '')}${r.last_error ? ' · ' + esc(r.last_error.slice(0, 60)) : ''}</td>
    <td class="muted" style="font-size:12px">${esc(new Date(r.updated_at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }))}</td>
  </tr>`).join('');
  return `<table><thead><tr><th>Submission</th><th>Status</th><th>Tries</th><th>Detail</th><th>Updated</th></tr></thead><tbody>${rows}</tbody></table>`;
}
